import {
    Button,
    Checkbox,
    Divider,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    SelectChangeEvent,
    Stack,
    Typography,
} from '@mui/material';
import { colours } from '../../../StyleConstants';
import { LassoTextField } from '../../../components/LassoTextField';
import { Check, Close } from '@mui/icons-material';
import LassoToggleButtons from '../../../components/LassoToggleButtons';
import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectSelectedDatabase } from '../../common/databaseSlice';
import {
    useGetSessionsQuery,
    useGetVisitByImagingQuery,
    useGetVisitByInstrumentQuery,
} from '../../../api/dashboardApiSlice';
import DataTable from './DataTable';
import PieChart from './PieChart';
import BarChart from './BarChart';
import { Virtuoso } from 'react-virtuoso';
import LoadingDashlet from "../../../components/loaders/LoadingDashlet";

type Session = {
    label: string;
    description: string;
}

type Props = {
    view: string;
    setView: Dispatch<SetStateAction<"chart" | "table">>;
}

const ResearchDomains = ({ view, setView }: Props) => {
    const database = useSelector(selectSelectedDatabase);

    const { data: constructs, isFetching: isFetchingConstructs } = useGetVisitByInstrumentQuery({ database: database })
    const { data: imaging, isFetching: isFetchingImaging } = useGetVisitByImagingQuery({ database: database })
    const { data: sessions, isFetching: isFetchingSessions } = useGetSessionsQuery({ database });

    const viewportRef = useRef<HTMLDivElement>(null);

    const [selectedDomain, setSelectedDomain] = useState("Imaging and Wearables");
    const [instrumentFilter, setInstrumentFilter] = useState('');
    const [ddAvailable, setDDAvailable] = useState(false)
    const [selectedSessionFilter, setSelectedSessionFilter] = useState<string[]>([]);

    const sessionInfoMap = useMemo(() => {
        if (!sessions) return {};
        const data: any = {}
        for (const session of sessions) {
            data[session.label] = session;
        }
        return data;
    }, [sessions]);

    useEffect(() => {
        setSelectedSessionFilter(sessions?.map(({ label }: Session) => label) || [])
    }, [sessions]);

    useEffect(() => {
        setSelectedDomain("Imaging and Wearables");
    }, [database]);

    const allSessionLabels = sessions?.map(({ label }: Session) => label);

    const handleSessionChange = (event: SelectChangeEvent<typeof selectedSessionFilter>) => {
        const {
            target: { value },
        } = event;
        if (value.includes("select-all-sessions")) {
            if (sessions.length === selectedSessionFilter.length) {
                setSelectedSessionFilter([]);
            } else {
                setSelectedSessionFilter(allSessionLabels);
            }
            return;
        } else {
            setSelectedSessionFilter(
                typeof value === 'string' ? value.split(',') : value,
            );
        }
    };

    const handleSessionToggleClick = () => {
        if (sessions.length === selectedSessionFilter.length) {
            setSelectedSessionFilter([]);
        } else {
            setSelectedSessionFilter(allSessionLabels);
        }
    }

    const renderChart = (chart: any, index: number) => {
        if (view === "table") {
            return <DataTable
                key={index}
                selectedSessionFilter={selectedDomain === "cohort-demographics-explorer" ? undefined : selectedSessionFilter}
                title={chart?.title}
                rowData={chart?.data}
                ddAvailable={ddAvailable}
                imagingData={imaging}
                eegData={constructs?.["Tabular EEG"]}
                columnDefs={[{
                    field: 'label',
                },
                { field: 'value' }]}/>
        }
        switch (chart.type) {
        case 'PIE':
            return (
                <PieChart
                    key={index}
                    data={chart?.data}
                    title={chart?.title}
                    selectedSessionFilter={selectedDomain === "cohort-demographics-explorer" ? undefined : selectedSessionFilter}
                    ddAvailable={ddAvailable}
                    imagingData={imaging}
                />
            )
        case 'BAR':
            return (
                <BarChart
                    key={index}
                    data={chart?.data}
                    source={chart?.source}
                    title={chart?.title}
                    selectedSessionFilter={selectedDomain === "cohort-demographics-explorer" ? undefined : selectedSessionFilter}
                    ddAvailable={ddAvailable}
                    imagingData={imaging}
                    eegData={constructs?.["Tabular EEG"]}
                    sessionInfoMap={sessionInfoMap}
                />
            )
        default:
            return <></>
        }
    }

    const getList = () => {
        if (instrumentFilter.length > 0) {
            const regex = new RegExp(instrumentFilter, 'i');
            let dashletData = imaging;
            Object.keys(constructs).forEach((key) => {
                dashletData = {
                    ...dashletData,
                    ...constructs[key]
                }
            })
            return Object.values(dashletData || {})
                //@ts-ignore
                .filter((dashlet) => regex.test(dashlet?.title?.toLowerCase()))

        }
        let dashletData: any
        switch (selectedDomain) {
        case "Imaging and Wearables":
            dashletData = imaging;
            break;
        case "All Domains":
            dashletData = imaging;
            Object.keys(constructs).forEach((key) => {
                dashletData = {
                    ...dashletData,
                    ...constructs[key]
                }
            })
            break;
        default:
            dashletData = constructs[selectedDomain];
            break;
        }
        return Object.values(dashletData || {})
    }

    if (isFetchingImaging || isFetchingConstructs || isFetchingSessions) {
        return <LoadingDashlet height={'calc(33% - 16px)'}/>
    }

    return (
        <Stack flexDirection={"row"} gap={3} width="100%" height={"calc(100% - 150px)"}>
            <Stack flexDirection={"column"} width="100%" height={"100%"} gap={2}>
                <Stack
                    marginTop={4}
                    flexDirection={"row"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                >

                    <Stack flexDirection={"row"} width={"100%"} gap={3} alignItems={"center"}>
                        <LassoTextField
                            sx={{ width: "100%", minWidth: "200px" }}
                            value={instrumentFilter}
                            onChange={(e) => setInstrumentFilter(e.target.value)}
                            size="small"
                            label={"Search Instrument"}
                        />
                        <div>
                            <FormControl sx={{ m: 1, width: 275 }}>
                                <InputLabel
                                    shrink={true}
                                    sx={{
                                        top: '-0.8rem',
                                        transform: "translate(8px, -9px) scale(0.75)",
                                    }}
                                >
                                    Session Filter
                                </InputLabel>
                                <Select
                                    id="dashboard-session-filter-dropdown"
                                    multiple
                                    value={selectedSessionFilter}
                                    onChange={handleSessionChange}
                                    input={<OutlinedInput label="Session Filter"/>}
                                    renderValue={() => `${selectedSessionFilter.length} session${selectedSessionFilter.length > 1 ? "s" : ""}`}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                maxHeight: 45 * 4.5 + 8,
                                                width: 250,
                                            },
                                        },
                                        MenuListProps: {
                                            sx: {
                                                paddingTop: 0,
                                                paddingBottom: 0,
                                            },
                                        },
                                    }}
                                    sx={{
                                        background: colours.core.white,
                                        height: "40px",
                                        boxShadow: "1px 2px 5px 0px rgba(21, 24, 24, 0.10)",
                                        "& fieldset": { border: 'none' },
                                        "& .Mui-disabled": { backgroundColor: colours.gray.gray20 },
                                        "&.Mui-focused": {
                                            border: "2px solid #80B7F8",
                                        },
                                        position: 'relative',
                                        borderRadius: '8px',
                                    }}
                                >
                                    {sessions?.map(({ label }: Session) => (
                                        <MenuItem key={label} value={label}
                                            id={`dashboard-session-filter-dropdown-item-${label}`}>
                                            <Checkbox checked={selectedSessionFilter.includes(label)}/>
                                            <ListItemText primary={label}/>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>
                        <Button
                            size="small"
                            variant={"outlined"}
                            color={sessions?.length === selectedSessionFilter?.length ? "error" : "primary"}
                            startIcon={sessions?.length === selectedSessionFilter?.length ? <Close/> : <Check/>}
                            disableRipple
                            onClick={handleSessionToggleClick}
                            onMouseDown={(e) => e.preventDefault()}
                            sx={{ minWidth: "206px" }}
                        >
                            {
                                sessions?.length === selectedSessionFilter?.length
                                    ? "Clear All Session(s)"
                                    : "Select All Sessions"
                            }
                        </Button>
                        <LassoToggleButtons
                            value={view}
                            onChange={(e, newValue) => {
                                if (newValue) {
                                    setView(newValue)
                                }
                            }}
                            exclusive
                            buttons={[
                                { label: "Chart View", value: "chart", minWidth: "200px" },
                                { label: "Table View", value: "table", minWidth: "200px" }
                            ]}/>
                    </Stack>

                </Stack>
                <Stack flexDirection="row" gap={3} height={"100%"}>
                    <Stack marginTop="16px" flexDirection="row" gap={3}>
                        <Stack gap={1} minWidth={"250px"}>
                            <Typography align={"center"} fontWeight={"bold"} marginRight={"24px"}>Select Research
                                Domains</Typography>
                            <Stack overflow={"auto"} gap='8px' paddingRight={3}>
                                {['All Domains', 'Imaging and Wearables'].concat(Object.keys(constructs)).map((domain: string) =>
                                    <Button
                                        key={domain}
                                        size="small"
                                        variant={"outlined"}
                                        disableRipple
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => {
                                            setSelectedDomain(domain)
                                            if (domain === "Imaging and Wearables") {
                                                setDDAvailable(false)
                                            } else {
                                                setDDAvailable(true)
                                            }
                                        }}
                                        sx={
                                            selectedDomain === domain
                                                ? {

                                                    color: colours.orange.orange,
                                                    borderColor: colours.orange.orange,
                                                    fontWeight: 'bold',
                                                    "&:hover": {
                                                        color: colours.orange.orange,
                                                        borderColor: colours.orange.orange,
                                                        fontWeight: 'bold',
                                                    }
                                                }
                                                : {}
                                        }
                                    >{domain}</Button>)}
                            </Stack>

                        </Stack>

                        <Divider orientation={"vertical"}/>
                    </Stack>
                    <Stack gap={3} width={"100%"}>
                        <Stack
                            marginTop="16px"
                            ref={viewportRef}
                            flexDirection="column"
                            overflow={"auto"}
                            width={"100%"}
                            height={"100%"}
                            paddingX={3}
                            sx={{
                                overflowAnchor: "none",
                            }}
                        >
                            <Virtuoso
                                style={{ height: '100%' }}
                                data={getList()}
                                itemContent={(index, user) => (
                                    renderChart(user, index)
                                )}
                            />
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
    )
}

export default ResearchDomains;
